import React, { useEffect, useRef, useState } from 'react';
import Iconify from '../../../../Iconify';
import useRealNode from '../../../../../hooks/useRealNode';
import VerificationInput from 'react-verification-input';

/* ---------- Account Verification component ----------  */

// Utils
import { publish } from '../../../../../utils/events';
import { ButtonCallToAction } from '../styled';
import API from '../../../../../utils/api';
import styles from './modal-verification-name.module.css';
import flagMexico from '../../assets/flag-mexico.png';
import phoneSMS from '../../assets/phone-verification-code.png';
import phoneSuccess from '../../assets/phone-verification-success.png';
import utils, { isVerified } from '../../../../../utils/utils';
import useUserContext from '../../../../../hooks/useUserContext';

const STEPS = {
  FORM: 'form',
  OTP: 'otp',
  SUCCESS: 'success',
};


export const Verification = () => {
  const { updateUserInfo } = useUserContext();

  const [show, setShow] = useState(false);
  const [step, setStep] = useState(STEPS.FORM);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const node = useRealNode('modal-node-portal');
  const resolveRef = useRef(null);

  // Estados del formulario
  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    phone: '',
  });

  // Estados de errores
  const [errors, setErrors] = useState({
    name: '',
    last_name: '',
    phone: '',
  });

  // Estado para código OTP
  const [otpCode, setOtpCode] = useState('');

  // Estado para countdown de reenvío
  const [countdown, setCountdown] = useState(0);

  // Estado para error de verificación SMS
  const [otpError, setOtpError] = useState('');

  // useEffect para manejar el countdown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleClose = event => {
    event.stopPropagation();
    event.preventDefault();

    if (isVerified) {
      handleContinue();
      return;
    }

    resolveRef.current(false);
    setShow(false);
    setStep(STEPS.FORM);
    setCountdown(0);
    setOtpError('');
  };

  const stopPropagation = event => {
    event.stopPropagation();
  };

  // Validaciones
  const validateName = value => {
    if (!value.trim()) {
      return 'El nombre es requerido';
    }
    if (value.trim().length < 2) {
      return 'El nombre debe tener al menos 2 caracteres';
    }
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
      return 'El nombre solo debe contener letras y espacios';
    }
    return '';
  };

  const validateLastName = value => {
    if (!value.trim()) {
      return 'El apellido es requerido';
    }
    if (value.trim().length < 2) {
      return 'El apellido debe tener al menos 2 caracteres';
    }
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)) {
      return 'El apellido solo debe contener letras y espacios';
    }
    return '';
  };

  const validatePhone = value => {
    const cleaned = value.replace(/\D/g, '');
    if (!cleaned) {
      return 'El teléfono es requerido';
    }
    if (cleaned.length !== 10) {
      return 'El teléfono debe tener 10 dígitos';
    }
    if (!/^\d+$/.test(cleaned)) {
      return 'El teléfono solo debe contener dígitos';
    }
    return '';
  };

  // Handlers
  const handleInputChange = (field, value) => {
    let processedValue = value;

    // Formateo especial para teléfono
    if (field === 'phone') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue,
    }));

    // Validación en tiempo real
    let error = '';
    switch (field) {
      case 'name':
        error = validateName(processedValue);
        break;
      case 'last_name':
        error = validateLastName(processedValue);
        break;
      case 'phone':
        error = validatePhone(processedValue);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateName(formData.name),
      last_name: validateLastName(formData.last_name),
      phone: validatePhone(formData.phone),
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some(error => error !== '');
  };

  const sendVerificationCode = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const smsData = {
        name: formData.name.trim(),
        last_name: formData.last_name.trim(),
        country: 'MX',
        phone: formData.phone.replace(/\D/g, ''),
        code: [null, null, null, null, null],
        smsCode: '',
      };

      await API.requestSms(smsData);
      setStep(STEPS.OTP);
      setCountdown(120); // Iniciar countdown de 120 segundos
      setOtpError(''); // Limpiar error al enviar nuevo código
    } catch (error) {
      console.error('Error al enviar código SMS:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return; // No hacer nada si el countdown está activo

    await sendVerificationCode();
  };

  const validateSMS = async () => {
    setIsSubmitting(true);
    try {
      const smsData = {
        name: formData.name.trim(),
        last_name: formData.last_name.trim(),
        country: 'MX',
        phone: formData.phone.replace(/\D/g, ''),
        code: otpCode.split(''),
      };

      const res = await API.verifySms(smsData);

      if (!res?.data?.success) {
        setIsSubmitting(false);
        setOtpError(
          res?.data?.error || 'El código ingresado es incorrecto. Por favor, intenta nuevamente.'
        );
        return;
      }

      setOtpError('');
      setStep(STEPS.SUCCESS);
      setIsVerifying(true);
      const userDataTemp = utils.getUserInfo();
      userDataTemp.identity_verified = true;
      const userBalance = utils.getUserBalance();
      const tempUser = {
        user: userDataTemp,
        balanceReal: userBalance.balanceReal,
        balancePcReal: userBalance.balancePcReal,
        balancePc: userBalance.balancePc,
      };
      publish('updateUserInfo', tempUser);
      updateUserInfo(tempUser);
      utils.setUserInfo(tempUser);
      utils.removeReadyToVerify();
    } catch (error) {
      console.error('Error al validar código SMS:', error);
      setOtpError('Ocurrió un error al verificar el código. Por favor, intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = () => {
    if (otpCode.length === 5) {
      validateSMS();
    }
  };

  const handleOtpChange = value => {
    setOtpCode(value);
    setOtpError('');
  };

  const handleContinue = () => {
    setShow(false);
    setStep(STEPS.FORM);
    setCountdown(0);
  };

  return (
    <div className={styles.verificationContainer}>
      <div className='text-center'>Antes de continuar, por favor verifica tu cuenta</div>

      {step === STEPS.FORM && (
        <div className={styles.infoMessage}>
          <p>
            Danos tu nombre(s) y apellidos completos, tal cual aparecen en tus identificacion
            oficiales.
          </p>
          <p>Estos datos serán comparados al momento de realizar alguna solicitud de retiro.</p>
        </div>
      )}

      {step === STEPS.FORM && (
        <form
          className={styles.form}
          onSubmit={e => {
            e.preventDefault();
            sendVerificationCode();
          }}
        >
          <div className={styles.row}>
            <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
              <label className={styles.label} htmlFor="name">
                Nombre(s)
              </label>
              <input
                id="name"
                type="text"
                className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                placeholder="Ingresa tu nombre"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                maxLength={50}
              />
              {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
            </div>

            <div className={`${styles.fieldGroup} ${styles.fieldGroupHalf}`}>
              <label className={styles.label} htmlFor="last_name">
                Apellido
              </label>
              <input
                id="last_name"
                type="text"
                className={`${styles.input} ${errors.last_name ? styles.inputError : ''}`}
                placeholder="Ingresa tu apellido"
                value={formData.last_name}
                onChange={e => handleInputChange('last_name', e.target.value)}
                maxLength={50}
              />
              {errors.last_name && <span className={styles.errorMessage}>{errors.last_name}</span>}
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="phone">
              Teléfono (México)
            </label>
            <div className={styles.phoneInputWrapper}>
              <div className={styles.phoneFlag}>
                <img src={flagMexico} alt="México" className={styles.flagImage} />
                <span className={styles.phoneCode}>+52</span>
              </div>
              <input
                id="phone"
                type="tel"
                className={`${styles.input} ${styles.phoneInput} ${
                  errors.phone ? styles.inputError : ''
                }`}
                placeholder="10 dígitos"
                value={formData.phone}
                onChange={e => handleInputChange('phone', e.target.value)}
                maxLength={10}
                inputMode="numeric"
              />
            </div>
            {errors.phone && <span className={styles.errorMessage}>{errors.phone}</span>}
          </div>

          <div className={styles.buttonContainer}>
            <ButtonCallToAction
              type="submit"
              className="guardar-picks"
              disabled={isSubmitting}
              style={{ width: 'auto', padding: '0.5em 1.5em' }}
            >
              {isSubmitting ? 'Enviando...' : 'ENVIAR CÓDIGO'}
            </ButtonCallToAction>
          </div>
        </form>
      )}

      {step === STEPS.OTP && (
        <div className={styles.otpContainer}>
          <div className={styles.otpTitle}>Ingresa el código de verificación</div>
          <img src={phoneSMS} alt="Phone SMS" className={styles.phoneSMSImage} />
          <div className={styles.otpWrapper}>
            <VerificationInput
              validChars="0-9"
              inputProps={{ type: 'tel' }}
              length={5}
              autoFocus
              value={otpCode}
              onChange={handleOtpChange}
              classNames={{
                container: 'sms-container',
                character: 'character',
                characterInactive: 'character--inactive',
                characterSelected: 'character--selected',
              }}
            />
          </div>
          <div className={styles.otpHelpText}>Escribe el código usando las casillas</div>
          {otpError && <div className={styles.errorMessage}>{otpError}</div>}

          <div className={styles.buttonContainer}>
            <ButtonCallToAction
              className="guardar-picks"
              as="a"
              href="https://wa.me/525552740236?text=Tengo%20problemas%20para%20verificar"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: 'auto',
                padding: '0.5em 2.5em',
                margin: 0,
              }}
            >
              Ayuda <Iconify icon="ic:baseline-help" />
            </ButtonCallToAction>
            <ButtonCallToAction
              type="button"
              onClick={handleVerifyCode}
              className="guardar-picks"
              disabled={otpCode.length !== 5 || isSubmitting}
              style={{ width: 'auto', padding: '0.5em 2.5em', margin: 0 }}
            >
              {isSubmitting ? 'Verificando...' : 'Verificar'}
              <Iconify icon="bitcoin-icons:verify-filled" style={{ fontSize: '1.5em' }} />
            </ButtonCallToAction>
          </div>

          <div className={styles.resendContainer}>
            <ButtonCallToAction
              type="button"
              onClick={handleResendCode}
              className="guardar-picks"
              disabled={countdown > 0 || isSubmitting}
              style={{
                backgroundColor: countdown > 0 ? '#787878' : undefined,
                width: 'auto',
                fontSize: '14px',
              }}
            >
              {countdown > 0
                ? `Reenviar Código en (${countdown} segundos)`
                : 'Reenviar Código ahora'}
            </ButtonCallToAction>
          </div>
        </div>
      )}

      {step === STEPS.SUCCESS && (
        <div className={styles.successContainer}>
          <img src={phoneSuccess} alt="Phone Success" className={styles.phoneSuccessImage} />
          <div className={styles.successTitle}>¡Código verificado!</div>
          <div className={styles.successDescription}>
            Tu cuenta ha sido verificada exitosamente.
          </div>
          <ButtonCallToAction
            type="button"
            onClick={handleContinue}
            className="guardar-picks"
            style={{ width: 'auto', padding: '0.5em 1.5em' }}
          >
            Continuar con Abono
          </ButtonCallToAction>
        </div>
      )}
    </div>
  );
};
